import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { api } from '../../api';
import { t } from '../../i18n';
import { showToast } from './toast';
import { icon } from './icons';
import { mediaUploadQuery, isImageKey } from '../../media-meta';
import { UploadQueue, type UploadTask } from '../../upload-queue';
import { saveZip, type ZipEntry } from '../../zip';

export interface AlbumTag { id: string; name: string; color: string; }
export interface AlbumInfo {
  id: string; name: string; description: string | null; cover_item_id?: string | null;
}
export interface AlbumItem {
  id: string; album_id: string; type: 'photo' | 'video'; file_url: string; thumbnail_url: string | null;
  original_filename: string; file_size: number | null; duration: number | null; width: number | null; height: number | null;
  taken_at: string | null; uploader_name: string | null; approved: number; created_at: string; tags?: AlbumTag[];
}

const mediaUrl = (key: string | null | undefined) => (key ? `/api/v1/media/${key}` : '');
function fmtSize(b: number | null): string {
  if (b == null) return '';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
}
function fmtDuration(s: number | null): string {
  if (s == null) return '';
  const m = Math.floor(s / 60), sec = s % 60;
  return `${m}:${sec.toString().padStart(2, '0')}`;
}

/**
 * <cadence-album-view> — the reusable single-album surface: item grid with Google-Photos
 * selection, upload queue, lightbox (with tags / approve / reject / delete), bulk ZIP
 * download and "set as cover". It talks to a configurable REST base (`apiBase`) so the
 * same UI backs the standalone Media gallery (/gallery), event galleries
 * (/events/:id/gallery) and the student portal (/portal/gallery, /portal/events/:id/gallery).
 * Capabilities are toggled per surface via the can* flags.
 *
 * Emits: `back`, `edit-album` (detail: album), `album-changed`, `album-deleted`
 * (detail: albumId), `tag-created` (detail: AlbumTag).
 */
@customElement('cadence-album-view')
export class CadenceAlbumView extends LitElement {
  /** REST base for album/item operations, e.g. "/gallery" or "/events/evt_x/gallery". */
  @property({ attribute: 'api-base' }) apiBase = '/gallery';
  @property({ attribute: 'album-id' }) albumId = '';
  @property({ type: Boolean }) canUpload = false;
  @property({ type: Boolean }) canApprove = false; // approve / reject / delete items
  @property({ type: Boolean }) canTag = false;
  @property({ type: Boolean }) canSetCover = false;
  @property({ type: Boolean }) canEditAlbum = false;
  @property({ type: Boolean }) canDeleteAlbum = false;
  @property({ type: Boolean }) showBack = false;
  @property({ attribute: 'back-label' }) backLabel = '';
  /** Submit-for-approval surface (portal) — tweaks copy + always reloads approved-only. */
  @property({ type: Boolean }) submitMode = false;
  @property({ attribute: false }) tags: AlbumTag[] = [];

  @state() private album: AlbumInfo | null = null;
  @state() private items: AlbumItem[] = [];
  @state() private loading = false;
  @state() private uploadTasks: UploadTask[] = [];
  private uploadQueue?: UploadQueue;

  // Selection (Google Photos-style) + download
  @state() private selected = new Set<string>();
  private lastIndex = -1;
  @state() private zip: { total: number; done: number; name: string } | null = null;

  // Lightbox
  @state() private lightbox: AlbumItem | null = null;
  @state() private lbFull = false;

  // Confirm dialog
  @state() private confirmOpen = false;
  @state() private confirmBusy = false;
  @state() private confirmWithReason = false;
  @state() private confirmConfig: { heading: string; message: string; confirmLabel: string } | null = null;
  private confirmAction: (() => Promise<void>) | null = null;

  static styles = css`
    :host { display: block; }
    .album-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; margin-bottom: var(--space-4); }
    .back { display: inline-flex; align-items: center; gap: 4px; background: none; border: none; cursor: pointer; color: var(--color-text-secondary); font: inherit; padding: 4px 0; margin-bottom: 6px; }
    .back:hover { color: var(--color-primary); }
    h2 { margin: 0; font-size: 1.35rem; }
    .desc { color: var(--color-text-secondary); font-size: 0.9rem; margin-top: 4px; }
    .acts { display: flex; gap: 6px; flex-shrink: 0; }
    .upload-wrap { margin-bottom: var(--space-4); }
    .item-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(150px, 1fr)); gap: 10px; }
    .item-tile { position: relative; aspect-ratio: 1; border-radius: var(--radius-md); overflow: hidden; background: var(--color-bg-secondary); cursor: pointer; border: 1px solid var(--color-border); }
    .item-tile img, .item-tile video { width: 100%; height: 100%; object-fit: cover; display: block; transition: transform 0.12s ease; }
    .item-tile .vid-badge { position: absolute; inset: 0; display: grid; place-items: center; pointer-events: none; }
    .item-tile .vid-badge .pill { background: rgba(0,0,0,0.55); color: #fff; border-radius: 999px; padding: 6px 8px; display: inline-flex; align-items: center; gap: 4px; font-size: 0.72rem; }
    .item-tile .vid-badge svg { width: 16px; height: 16px; }
    .item-tile.selected { background: var(--color-bg-secondary); }
    .item-tile.selected img, .item-tile.selected video { transform: scale(0.85); border-radius: 8px; box-shadow: 0 0 0 3px var(--color-primary); }
    .sel-check { position: absolute; top: 6px; left: 6px; width: 22px; height: 22px; padding: 0; border-radius: 50%; background: rgba(255,255,255,0.6); border: 2px solid rgba(255,255,255,0.9); box-shadow: 0 0 2px rgba(0,0,0,0.4); display: grid; place-items: center; cursor: pointer; opacity: 0; transition: opacity 0.12s; z-index: 2; color: rgba(0,0,0,0.45); }
    .item-tile:hover .sel-check { opacity: 1; }
    .item-grid.selecting .sel-check { opacity: 1; }
    .sel-check:hover { background: #fff; border-color: #fff; color: rgba(0,0,0,0.75); opacity: 1; }
    .sel-check.on { background: var(--color-primary); border-color: var(--color-primary); opacity: 1; color: #fff; }
    .sel-check svg { width: 14px; height: 14px; }
    .pend-badge { position: absolute; bottom: 6px; left: 6px; background: var(--color-warning, #d97706); color: #fff; border-radius: 999px; font-size: 0.66rem; font-weight: 700; padding: 2px 7px; z-index: 2; pointer-events: none; }
    .cover-pill { position: absolute; top: 6px; right: 6px; display: inline-flex; align-items: center; gap: 3px; background: var(--color-primary); color: #fff; border-radius: 999px; font-size: 0.68rem; font-weight: 700; padding: 2px 8px; z-index: 2; pointer-events: none; }
    .cover-pill svg { width: 12px; height: 12px; }
    .sel-bar { display: flex; align-items: center; gap: 10px; margin-bottom: var(--space-3); padding: 8px 10px; border: 1px solid var(--color-border); border-radius: var(--radius-md); background: var(--color-bg-secondary); flex-wrap: wrap; }
    .sel-bar .sel-count { font-weight: 600; font-size: 0.9rem; }
    .sel-bar .sel-link { background: none; border: none; color: var(--color-primary); cursor: pointer; font: inherit; font-size: 0.85rem; }
    .sel-bar .sel-spacer { flex: 1; }
    .sel-bar .icon-btn { background: none; border: none; cursor: pointer; color: var(--color-text-secondary); display: inline-flex; padding: 2px; }
    .sel-bar .icon-btn:hover { color: var(--color-text-primary); }
    .zip-bar { margin-bottom: var(--space-4); padding: 8px 10px; border: 1px solid var(--color-border); border-radius: var(--radius-md); background: var(--color-bg-primary); font-size: 0.85rem; }
    .zip-track { height: 6px; border-radius: 999px; background: var(--color-bg-secondary); overflow: hidden; margin-top: 6px; }
    .zip-track > span { display: block; height: 100%; background: var(--color-primary); transition: width 0.15s ease; }
    .lb-stage { position: relative; display: grid; place-items: center; background: var(--color-bg-secondary); border-radius: var(--radius-md); max-height: 60vh; overflow: hidden; }
    .lb-stage img, .lb-stage video { max-width: 100%; max-height: 60vh; object-fit: contain; }
    .lb-preload { position: absolute; width: 1px; height: 1px; opacity: 0; pointer-events: none; }
    .lb-footer { display: flex; flex: 1; flex-wrap: wrap; gap: 10px; align-items: center; justify-content: space-between; }
    .lb-group { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
    .lb-meta { margin-top: var(--space-3); color: var(--color-text-secondary); font-size: 0.85rem; }
    .tag-block { margin-top: 12px; }
    .tag-block label { display: block; font-size: 0.82rem; color: var(--color-text-secondary); margin-bottom: 4px; }
    .efield { margin-bottom: var(--space-3); }
    textarea { width: 100%; box-sizing: border-box; font: inherit; padding: 8px 10px; border: 1px solid var(--color-border); border-radius: var(--radius-md); background: var(--color-bg-primary); color: var(--color-text-primary); resize: vertical; min-height: 70px; }
  `;

  updated(changed: Map<string, unknown>) {
    if ((changed.has('albumId') || changed.has('apiBase')) && this.albumId) this.load();
  }

  private async load() {
    this.loading = true; this.items = []; this.clearSelection();
    try {
      const res = await api.get<{ album: AlbumInfo; items: AlbumItem[] }>(`${this.apiBase}/albums/${this.albumId}`);
      this.album = res.album; this.items = res.items;
    } catch (e) { showToast((e as Error).message, 'error'); }
    finally { this.loading = false; }
  }

  /** Reload items in place (after upload/approve/delete) and tell the parent counts changed.
   *  Public so a parent can refresh after editing the album's name/description. */
  async reload() {
    try {
      const res = await api.get<{ album: AlbumInfo; items: AlbumItem[] }>(`${this.apiBase}/albums/${this.albumId}`);
      this.album = res.album; this.items = res.items;
    } catch (e) { showToast((e as Error).message, 'error'); }
    this.emit('album-changed');
  }

  private emit(name: string, detail?: unknown) {
    this.dispatchEvent(new CustomEvent(name, { detail, bubbles: true, composed: true }));
  }

  // ---- Selection ----
  private get selectionActive(): boolean { return this.selected.size > 0; }
  private clearSelection() { this.selected = new Set(); this.lastIndex = -1; }
  private selectAllItems() { this.selected = new Set(this.items.map((i) => i.id)); }
  private toggleSelect(index: number, shift: boolean) {
    const sel = new Set(this.selected);
    if (shift && this.lastIndex >= 0) {
      const [a, b] = this.lastIndex < index ? [this.lastIndex, index] : [index, this.lastIndex];
      for (let i = a; i <= b; i++) sel.add(this.items[i].id);
    } else {
      const id = this.items[index].id;
      if (sel.has(id)) sel.delete(id); else sel.add(id);
      this.lastIndex = index;
    }
    this.selected = sel;
  }
  private onTileClick(e: MouseEvent, index: number) {
    if (this.selectionActive) this.toggleSelect(index, e.shiftKey);
    else this.openLightbox(this.items[index]);
  }

  /** Bundle the selection into one ZIP via the native Save dialog (each file streams from
   *  R2 through the browser-side zip writer — the Worker only serves individual files). */
  private async downloadSelected() {
    const sel = this.items.filter((it) => this.selected.has(it.id));
    if (!sel.length) return;
    const entries: ZipEntry[] = sel.map((it) => ({
      name: it.original_filename || it.id,
      stream: async () => {
        const r = await fetch(mediaUrl(it.file_url), { credentials: 'include' });
        if (!r.ok || !r.body) throw new Error(t('ui.album.fetchFailed', { name: it.original_filename || it.id }));
        return r.body;
      },
    }));
    const zipName = `${(this.album?.name || 'media').replace(/[\\/:*?"<>|]/g, '_')}.zip`;
    this.zip = { total: entries.length, done: 0, name: '' };
    try {
      const res = await saveZip(zipName, entries, (i, n, phase) => {
        this.zip = { total: entries.length, done: phase === 'done' ? i + 1 : i, name: n };
      });
      if (res === 'saved') {
        const items = entries.length === 1 ? t('ui.album.itemOne') : t('ui.album.itemOther');
        showToast(t('ui.album.downloaded', { count: entries.length, items, name: zipName }), 'success');
        this.clearSelection();
      }
    } catch (e) {
      showToast(t('ui.album.downloadFailed', { message: (e as Error).message }), 'error');
    } finally {
      this.zip = null;
    }
  }

  private async setAsCover() {
    if (this.selected.size !== 1 || !this.album) return;
    const id = [...this.selected][0];
    try {
      await api.post(`${this.apiBase}/albums/${this.album.id}/cover`, { item_id: id });
      this.album = { ...this.album, cover_item_id: id };
      showToast(t('ui.album.coverUpdated'), 'success');
      this.clearSelection();
      this.emit('album-changed');
    } catch (e) { showToast((e as Error).message, 'error'); }
  }

  private removeSelected() {
    const ids = [...this.selected];
    if (!ids.length) return;
    const items = ids.length === 1 ? t('ui.album.itemOne') : t('ui.album.itemOther');
    this.askConfirm({ heading: t('ui.album.deleteItemsHeading'), message: t('ui.album.deleteItemsMessage', { count: ids.length, items }), confirmLabel: t('ui.album.delete') }, async () => {
      for (const id of ids) await api.del(`${this.apiBase}/items/${id}`);
      this.clearSelection();
      await this.reload();
    });
  }

  // ---- Upload ----
  private ensureQueue(): UploadQueue {
    if (this.uploadQueue) return this.uploadQueue;
    this.uploadQueue = new UploadQueue({
      concurrency: 3,
      buildPath: (file, meta, albumId) => `${this.apiBase}/albums/${albumId}/items?${mediaUploadQuery(file, meta)}`,
      onUploaded: async (item: any, _file, meta) => {
        if (meta.thumbnailBlob && item?.id) await api.upload(`${this.apiBase}/items/${item.id}/thumbnail`, meta.thumbnailBlob);
      },
      onChange: () => { this.uploadTasks = [...this.uploadQueue!.tasks]; },
      onBatchComplete: () => {
        const done = this.uploadTasks.filter((t) => t.status === 'done').length;
        const failed = this.uploadTasks.filter((t) => t.status === 'error').length;
        if (done > 0) {
          const items = done > 1 ? t('ui.album.itemOther') : t('ui.album.itemOne');
          const failedSuffix = failed ? t('ui.album.failedSuffix', { count: failed }) : '';
          const msg = this.submitMode
            ? t('ui.album.submitted', { count: done, items, failed: failedSuffix })
            : t('ui.album.uploaded', { count: done, items, failed: failedSuffix });
          showToast(msg, failed ? 'error' : 'success');
        }
        this.reload();
      },
      onUnsupported: (f) => showToast(t('ui.album.skippedUnsupported', { name: f.name }), 'error'),
    });
    return this.uploadQueue;
  }
  private onFiles(files: File[]) {
    if (!this.album) return;
    this.ensureQueue().add(files, this.album.id);
  }

  // ---- Items ----
  private openLightbox(it: AlbumItem) { this.lightbox = it; this.lbFull = false; }
  private downloadItem(it: AlbumItem) {
    const a = document.createElement('a');
    a.href = mediaUrl(it.file_url);
    a.download = it.original_filename || 'download';
    document.body.appendChild(a); a.click(); a.remove();
  }
  private openItem(it: AlbumItem) { window.open(mediaUrl(it.file_url), '_blank', 'noopener'); }

  private deleteItem(it: AlbumItem) {
    this.askConfirm({ heading: t('ui.album.deleteItemHeading'), message: t('ui.album.deleteItemMessage', { name: it.original_filename }), confirmLabel: t('ui.album.delete') }, async () => {
      await api.del(`${this.apiBase}/items/${it.id}`);
      this.lightbox = null;
      await this.reload();
    });
  }
  private async approve(it: AlbumItem) {
    try {
      await api.post(`${this.apiBase}/items/${it.id}/approve`, { tag_ids: (it.tags ?? []).map((t) => t.id) });
      showToast(t('ui.album.approved'), 'success');
      if (this.lightbox?.id === it.id) this.lightbox = null;
      await this.reload();
    } catch (e) { showToast((e as Error).message, 'error'); }
  }
  private reject(it: AlbumItem) {
    this.askConfirm({ heading: t('ui.album.rejectHeading'), message: t('ui.album.rejectMessage', { name: it.original_filename }), confirmLabel: t('ui.album.reject') }, async () => {
      const reason = (this.renderRoot.querySelector('#reject-reason') as HTMLTextAreaElement | null)?.value?.trim() || undefined;
      await api.post(`${this.apiBase}/items/${it.id}/reject`, { reason });
      this.lightbox = null;
      await this.reload();
    }, true);
  }

  private async saveItemTags(it: AlbumItem, tagIds: string[]) {
    try {
      const updated = await api.put<AlbumItem>(`${this.apiBase}/items/${it.id}/tags`, { tag_ids: tagIds });
      this.items = this.items.map((x) => (x.id === it.id ? { ...x, tags: updated.tags } : x));
      if (this.lightbox?.id === it.id) this.lightbox = { ...this.lightbox, tags: updated.tags };
    } catch (e) { showToast((e as Error).message, 'error'); }
  }
  private addTag(it: AlbumItem, tag: AlbumTag) {
    const ids = new Set((it.tags ?? []).map((t) => t.id)); ids.add(tag.id);
    this.saveItemTags(it, [...ids]);
  }
  private removeTag(it: AlbumItem, tagId: string) {
    this.saveItemTags(it, (it.tags ?? []).map((t) => t.id).filter((id) => id !== tagId));
  }
  private async createTag(it: AlbumItem, name: string) {
    try {
      const tag = await api.post<AlbumTag>(`${this.apiBase}/tags`, { name });
      this.emit('tag-created', tag);
      this.addTag(it, tag);
    } catch (e) { showToast((e as Error).message, 'error'); }
  }

  // ---- Album-level ----
  private deleteAlbum() {
    if (!this.album) return;
    this.askConfirm({ heading: t('ui.album.deleteAlbumHeading'), message: t('ui.album.deleteAlbumMessage', { name: this.album.name }), confirmLabel: t('ui.album.deleteAlbum') }, async () => {
      const id = this.album!.id;
      await api.del(`${this.apiBase}/albums/${id}`);
      this.emit('album-deleted', id);
    });
  }

  // ---- Confirm ----
  private askConfirm(config: { heading: string; message: string; confirmLabel: string }, action: () => Promise<void>, withReason = false) {
    this.confirmConfig = config; this.confirmAction = action; this.confirmWithReason = withReason; this.confirmOpen = true;
  }
  private closeConfirm() { if (this.confirmBusy) return; this.confirmOpen = false; this.confirmAction = null; }
  private async runConfirm() {
    if (!this.confirmAction) return;
    this.confirmBusy = true;
    try { await this.confirmAction(); this.confirmOpen = false; this.confirmAction = null; }
    catch (e) { showToast((e as Error).message, 'error'); }
    finally { this.confirmBusy = false; }
  }

  render() {
    const a = this.album;
    return html`
      ${this.showBack ? html`<button class="back" @click=${() => this.emit('back')}>${icon('chevron-left', 16)} ${this.backLabel || t('ui.album.allAlbums')}</button>` : ''}
      <div class="album-head">
        <div>
          <h2>${a?.name ?? ''}</h2>
          ${a?.description ? html`<div class="desc">${a.description}</div>` : ''}
        </div>
        ${this.canEditAlbum || this.canDeleteAlbum ? html`
          <div class="acts">
            ${this.canEditAlbum ? html`<cadence-button variant="ghost" size="sm" @click=${() => this.emit('edit-album', a)}>${t('ui.album.edit')}</cadence-button>` : ''}
            ${this.canDeleteAlbum ? html`<cadence-button variant="danger" size="sm" @click=${() => this.deleteAlbum()}>${t('ui.album.delete')}</cadence-button>` : ''}
          </div>` : ''}
      </div>

      ${this.canUpload ? html`
        <div class="upload-wrap">
          <cadence-file-upload multiple
            label=${this.submitMode ? t('ui.album.uploadSubmit') : t('ui.album.uploadDrop')}
            @files=${(e: CustomEvent) => this.onFiles(e.detail)}></cadence-file-upload>
        </div>
        ${this.uploadTasks.length ? html`
          <div class="upload-wrap">
            <cadence-upload-queue .tasks=${this.uploadTasks}
              @cancel=${(e: CustomEvent) => this.uploadQueue?.cancel(e.detail)}
              @retry=${(e: CustomEvent) => this.uploadQueue?.retry(e.detail)}
              @clear=${() => this.uploadQueue?.clearFinished()}></cadence-upload-queue>
          </div>` : ''}` : ''}

      ${this.selectionActive ? html`
        <div class="sel-bar">
          <button class="icon-btn" title=${t('ui.album.clearSelection')} @click=${() => this.clearSelection()}>${icon('x', 18)}</button>
          <span class="sel-count">${t('ui.album.selected', { count: this.selected.size })}</span>
          <button class="sel-link" @click=${() => this.selectAllItems()}>${t('ui.album.selectAll', { count: this.items.length })}</button>
          <span class="sel-spacer"></span>
          ${this.canSetCover && this.selected.size === 1 ? html`<cadence-button size="sm" variant="ghost" @click=${() => this.setAsCover()}>${icon('image', 16)} ${t('ui.album.setAsCover')}</cadence-button>` : ''}
          <cadence-button size="sm" @click=${() => this.downloadSelected()}>${icon('download', 16)} ${t('ui.album.download')}</cadence-button>
          ${this.canApprove ? html`<cadence-button size="sm" variant="danger" @click=${() => this.removeSelected()}>${icon('trash-2', 16)} ${t('ui.album.remove')}</cadence-button>` : ''}
        </div>` : ''}

      ${this.zip ? html`
        <div class="zip-bar">
          <span>${t('ui.album.preparingZip', { done: this.zip.done, total: this.zip.total })}${this.zip.name ? ` · ${this.zip.name}` : ''}</span>
          <div class="zip-track"><span style="width:${Math.round((this.zip.done / Math.max(1, this.zip.total)) * 100)}%"></span></div>
        </div>` : ''}

      ${this.loading ? html`<cadence-loading></cadence-loading>`
        : this.items.length ? html`
          <div class="item-grid ${this.selectionActive ? 'selecting' : ''}">
            ${this.items.map((it, i) => this.renderTile(it, i))}
          </div>`
        : html`<cadence-empty-state heading=${t('ui.album.emptyHeading')} message=${this.canUpload ? (this.submitMode ? t('ui.album.emptySubmit') : t('ui.album.emptyUpload')) : t('ui.album.emptyNothing')}></cadence-empty-state>`}

      ${this.renderLightbox()}
      ${this.renderConfirm()}
    `;
  }

  private renderTile(it: AlbumItem, index: number) {
    const usePoster = it.type === 'photo' || isImageKey(it.thumbnail_url);
    const sel = this.selected.has(it.id);
    return html`
      <div class="item-tile ${sel ? 'selected' : ''}" @click=${(e: MouseEvent) => this.onTileClick(e, index)}>
        <button class="sel-check ${sel ? 'on' : ''}" title=${t('ui.album.select')}
          @click=${(e: MouseEvent) => { e.stopPropagation(); this.toggleSelect(index, e.shiftKey); }}>${icon('check', 14)}</button>
        ${usePoster
          ? html`<img src=${mediaUrl(it.thumbnail_url || it.file_url)} alt=${it.original_filename} loading="lazy" />`
          : html`<video src=${mediaUrl(it.file_url)} preload="metadata" muted></video>`}
        ${it.type === 'video' ? html`<div class="vid-badge"><span class="pill">${icon('play', 16)} ${fmtDuration(it.duration)}</span></div>` : ''}
        ${!it.approved ? html`<span class="pend-badge">${t('ui.album.pending')}</span>` : ''}
        ${this.album?.cover_item_id === it.id ? html`<span class="cover-pill">${icon('check', 12)} ${t('ui.album.cover')}</span>` : ''}
      </div>`;
  }

  private renderLightbox() {
    const it = this.lightbox;
    if (!it) return '';
    return html`
      <cadence-modal ?open=${!!it} heading=${it.original_filename} @close=${() => (this.lightbox = null)}>
        <div class="lb-stage">
          ${it.type === 'photo'
            ? html`
                <img src=${this.lbFull ? mediaUrl(it.file_url) : mediaUrl(it.thumbnail_url || it.file_url)} alt=${it.original_filename} />
                ${!this.lbFull && it.thumbnail_url && it.thumbnail_url !== it.file_url
                  ? html`<img class="lb-preload" src=${mediaUrl(it.file_url)} @load=${() => (this.lbFull = true)} aria-hidden="true" />`
                  : ''}`
            : html`<video src=${mediaUrl(it.file_url)} controls preload="metadata"></video>`}
        </div>
        <div class="lb-meta">
          ${it.taken_at ? `${t('ui.album.taken', { date: it.taken_at.slice(0, 10) })} · ` : ''}
          ${it.type === 'video' && it.duration != null ? `${fmtDuration(it.duration)} · ` : ''}
          ${it.width && it.height ? `${it.width}×${it.height} · ` : ''}
          ${fmtSize(it.file_size)}${it.uploader_name ? ` · ${it.uploader_name}` : ''}
          ${it.approved ? '' : ` · ${t('ui.album.awaitingApproval')}`}
        </div>
        ${this.canTag ? html`
          <div class="tag-block">
            <label>${t('ui.album.tags')}</label>
            <cadence-tag-picker
              .selected=${it.tags ?? []}
              .options=${this.tags}
              placeholder=${t('ui.album.tagPlaceholder')}
              @select=${(e: CustomEvent) => this.addTag(it, e.detail)}
              @create=${(e: CustomEvent) => this.createTag(it, e.detail)}
              @remove=${(e: CustomEvent) => this.removeTag(it, e.detail)}></cadence-tag-picker>
          </div>` : ''}
        <div slot="footer" class="lb-footer">
          <div class="lb-group">
            <cadence-button variant="ghost" @click=${() => this.openItem(it)}>${t('ui.album.openFull')}</cadence-button>
            <cadence-button variant="ghost" @click=${() => this.downloadItem(it)}>${t('ui.album.download')}</cadence-button>
          </div>
          <div class="lb-group">
            ${!it.approved && this.canApprove ? html`
              <cadence-button variant="danger" @click=${() => this.reject(it)}>${t('ui.album.reject')}</cadence-button>
              <cadence-button @click=${() => this.approve(it)}>${t('ui.album.approve')}</cadence-button>`
              : this.canApprove ? html`<cadence-button variant="danger" @click=${() => this.deleteItem(it)}>${t('ui.album.delete')}</cadence-button>` : ''}
            <cadence-button variant="ghost" @click=${() => (this.lightbox = null)}>${t('ui.album.close')}</cadence-button>
          </div>
        </div>
      </cadence-modal>`;
  }

  private renderConfirm() {
    return html`
      <cadence-modal ?open=${this.confirmOpen} heading=${this.confirmConfig?.heading ?? ''} @close=${() => this.closeConfirm()}>
        <p style="margin:0">${this.confirmConfig?.message ?? ''}</p>
        ${this.confirmWithReason ? html`<div class="efield" style="margin-top:12px"><textarea id="reject-reason" placeholder=${t('ui.album.reasonPlaceholder')}></textarea></div>` : ''}
        <div slot="footer">
          <cadence-button variant="ghost" ?disabled=${this.confirmBusy} @click=${() => this.closeConfirm()}>${t('common.actions.cancel')}</cadence-button>
          <cadence-button variant="danger" ?loading=${this.confirmBusy} @click=${() => this.runConfirm()}>${this.confirmConfig?.confirmLabel ?? t('ui.album.confirm')}</cadence-button>
        </div>
      </cadence-modal>`;
  }
}
